import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree, CanActivate } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard extends KeycloakAuthGuard implements CanActivate {
  constructor(
    protected readonly router: Router,
    protected readonly keycloak: KeycloakService
  ) {
    super(router, keycloak);
  }

  // Override canActivate to handle local mode before KeycloakAuthGuard tries anything
  override async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Skip auth entirely in local mode - bypass KeycloakAuthGuard completely
    const skipAuth = window['env']?.['skipAuth'] || window['env']?.['localMode'];
    if (skipAuth) {
      console.log('AuthGuard - Local mode, skipping auth');
      return true;
    }

    // Otherwise, use the normal Keycloak flow
    return super.canActivate(route, state);
  }

  async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // If API is unreachable, redirect to error page (not /init)
    if (ConfigService.apiError) {
      console.log('AuthGuard - API unreachable, redirecting to /error');
      this.router.navigate(['/error']);
      return false;
    }
    // CRITICAL: If not initialized, skip ALL Keycloak logic and redirect to init
    if (!ConfigService.initialized) {
      console.log('AuthGuard - Not initialized, redirecting to /init');
      this.router.navigate(['/init']);
      return false;
    }

    const isInitRoute = state.url.startsWith('/init');

    if (!this.authenticated && !isInitRoute) {
      console.log('AuthGuard - Not authenticated, access denied to:', state.url);
      // return false; // Prevent access, don't auto-login
      await this.keycloak.login({
        redirectUri: window.location.origin + state.url
      });
    }

    try {
      const token = await this.keycloak.getToken();
      localStorage.setItem('id_token', token);
      const decodedJWT = JSON.parse(window.atob(token.split('.')[1]));
      console.log('AuthGuard - Token retrieved and stored');
    }
    catch(error) {
      console.error('AuthGuard - Error getting token:', error);
      this.keycloak.logout(window.location.href);
    }
    return this.authenticated;
  }

  logout() {
    console.log('logout');
    // remove user from local storage to log user out
    localStorage.removeItem('id_token');
    localStorage.removeItem('me');
    this.keycloak.logout(window.location.href);
  }
}
